import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BusReport = () => {
  const currentSessionId = localStorage.getItem('currentSessionId');

  const [busSchedules, setBusSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('');
  const [busNumbers, setBusNumbers] = useState([]);
  const [selectedBusNumber, setSelectedBusNumber] = useState('');
  const [students, setStudents] = useState([]);

  /* ---------------- FETCH BUS SCHEDULES ---------------- */
  useEffect(() => {
    if (!currentSessionId) return;

    const fetchBusSchedules = async () => {
      try {
        const res = await axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/busSchedules?session=${currentSessionId}`);

        const data = res.data || [];
        setBusSchedules(data);

        // Unique routes (session-based)
        const uniqueRoutes = [...new Set(data.map(bus => bus.route))];
        setRoutes(uniqueRoutes);
      } catch (err) {
        setError('Failed to fetch bus schedules.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBusSchedules();
  }, [currentSessionId]);

  /* ---------------- ROUTE → BUS NUMBERS ---------------- */
  useEffect(() => {
    if (selectedRoute) {
      const filteredBuses = busSchedules
        .filter(bus => bus.route === selectedRoute)
        .map(bus => bus.busNumber);

      setBusNumbers(filteredBuses);
    } else {
      setBusNumbers([]);
      setSelectedBusNumber('');
      setStudents([]);
    }
  }, [selectedRoute, busSchedules]);

  /* ---------------- BUS → STUDENTS ---------------- */
  const handleBusSelection = (busNumber) => {
    setSelectedBusNumber(busNumber);

    const busDetail = busSchedules.find(
      bus => bus.busNumber === busNumber
    );

    setStudents(busDetail?.students || []);
  };

  /* ---------------- SUMMARY ---------------- */
  const totalBuses = busSchedules.length;

  const totalStudents = useMemo(
    () =>
      busSchedules.reduce(
        (sum, bus) => sum + (bus.students?.length || 0),
        0
      ),
    [busSchedules]
  );

  /* ---------------- EXPORT HELPERS ---------------- */
  const exportToExcel = (data, filename) => {
    if (!data.length) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, filename);
  };

  const handleExportBusSchedules = () => {
    const data = busSchedules.map(bus => ({
      'Bus Number': bus.busNumber,
      Route: bus.route,
      'Departure Time': bus.departureTime,
      'Arrival Time': bus.arrivalTime,
      'Driver Name': bus.driverName,
      'Contact Number': bus.contactNumber,
      'Student Capacity': bus.studentCapacity,
      'Allocated Students': bus.students?.length || 0,
      Session: bus.sessionId,
    }));

    exportToExcel(data, 'Bus_Schedules.xlsx');
  };

  const handleExportStudents = () => {
    const data = students.map((s, i) => ({
      'Sr No.': i + 1,
      Name: s.name,
      'Class (Section)': `${s.class} (${s.section})`,
      Route: s.route,
      Location: s.location,
      'Bus Number': selectedBusNumber,
      Session: currentSessionId,
    }));

    exportToExcel(data, 'Bus_Students.xlsx');
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6">

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-green-100 p-4 rounded">
            <h4 className="text-blue-700 font-semibold">Total Buses</h4>
            <p className="text-2xl font-bold text-blue-800">{totalBuses}</p>
          </div>
          <div className="bg-green-100 p-4 rounded shadow">
            <h4 className="text-green-700 font-semibold">Total Students</h4>
            <p className="text-2xl font-bold text-green-800">{totalStudents}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Filters for Alloted Students</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <label className="block mb-1 font-medium text-gray-700">
                Select Route
              </label>
              <select
                className="w-full border rounded px-3 py-2"
                value={selectedRoute}
                onChange={(e) => setSelectedRoute(e.target.value)}
              >
                <option value="">-- All Routes --</option>
                {routes.map((route, i) => (
                  <option key={i} value={route}>{route}</option>
                ))}
              </select>
            </div>

            {selectedRoute && (
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Select Bus Number
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={selectedBusNumber}
                  onChange={(e) => handleBusSelection(e.target.value)}
                >
                  <option value="">-- Select Bus --</option>
                  {busNumbers.map((bus, i) => (
                    <option key={i} value={bus}>{bus}</option>
                  ))}
                </select>
              </div>
            )}

          </div>
        </div>

        {/* Bus Table */}
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Bus Schedules</h3>
            <button
              onClick={handleExportBusSchedules}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Export Bus Schedules
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border">
              <thead className="bg-gray-200">
                <tr>
                  <th className="border px-4 py-2">Sr No.</th>
                  <th className="border px-4 py-2">Bus Number</th>
                  <th className="border px-4 py-2">Route</th>
                  <th className="border px-4 py-2">Departure Time</th>
                  <th className="border px-4 py-2">Arrival Time</th>
                  <th className="border px-4 py-2">Driver Name</th>
                  <th className="border px-4 py-2">Contact Number</th>
                  <th className="border px-4 py-2">Capacity</th>
                  <th className="border px-4 py-2">Allocated Students</th>
                </tr>
              </thead>
              <tbody>
                {busSchedules.map((bus, i) => (
                  <tr key={bus._id} className="text-center">
                    <td className="border px-4 py-2">{i + 1}</td>
                    <td className="border px-4 py-2">{bus.busNumber}</td>
                    <td className="border px-4 py-2">{bus.route}</td>
                    <td className="border px-4 py-2">{bus.departureTime}</td>
                    <td className="border px-4 py-2">{bus.arrivalTime}</td>
                    <td className="border px-4 py-2">{bus.driverName}</td>
                    <td className="border px-4 py-2">{bus.contactNumber}</td>
                    <td className="border px-4 py-2">{bus.studentCapacity}</td>
                    <td className="border px-4 py-2">
                      {bus.students?.length || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Students Table */}
        {selectedBusNumber && students.length > 0 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Students in Bus {selectedBusNumber}
              </h3>
              <button
                onClick={handleExportStudents}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Export Student Details
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="border px-4 py-2">Sr No.</th>
                    <th className="border px-4 py-2">Name</th>
                    <th className="border px-4 py-2">Class (Section)</th>
                    <th className="border px-4 py-2">Route</th>
                    <th className="border px-4 py-2">Location</th>
                    <th className="border px-4 py-2">Bus Number</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, i) => (
                    <tr key={i} className="text-center">
                      <td className="border px-4 py-2">{i + 1}</td>
                      <td className="border px-4 py-2">{s.name}</td>
                      <td className="border px-4 py-2">
                        {`${s.class} (${s.section})`}
                      </td>
                      <td className="border px-4 py-2">{s.route}</td>
                      <td className="border px-4 py-2">{s.location}</td>
                      <td className="border px-4 py-2">{selectedBusNumber}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default BusReport;
